import { useEffect, useRef, useState } from "react";
import {
  getColumnData,
  isCardData,
  isCardDropTargetData,
  isColumnData,
  isDraggingACard,
  isDraggingAColumn,
  isShallowEqual,
} from "./types";
import { autoScrollForElements } from "@atlaskit/pragmatic-drag-and-drop-auto-scroll/element";
import { unsafeOverflowAutoScrollForElements } from "@atlaskit/pragmatic-drag-and-drop-auto-scroll/unsafe-overflow/element";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { preserveOffsetOnSource } from "@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source";
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import { DragLocationHistory } from "@atlaskit/pragmatic-drag-and-drop/types";
import { Box, Typography, useTheme } from "@mui/material";
import { Card, DragIndicator } from "./Card";

export type IColumn = {
  id: string;
  title?: string;
  rightComponent?: React.ReactNode;
  cards: any[];
  updateLayout?: (layout: any[]) => void;
  onRemoveCard?: (idx: number) => void;
  onHideCard?: (idx: number) => void;
  border?: boolean;
  renderCard?: (card: any, idx: any) => React.ReactNode;
  onClick?: () => void;
  selected?: boolean;
};

type ColumnProps = {
  column: IColumn;
};

type ColumnState =
  | {
      type: "is-card-over";
      isOverChildCard: boolean;
      dragging: DOMRect;
    }
  | {
      type: "is-column-over";
    }
  | {
      type: "idle";
    }
  | {
      type: "is-dragging";
    };

const idle: ColumnState = { type: "idle" };

export const Column = (props: ColumnProps) => {
  const { column } = props;
  const {
    id,
    title,
    rightComponent,
    cards,
    updateLayout,
    onRemoveCard,
    onHideCard,
    border = true,
    renderCard,
    onClick,
    selected = false,
  } = column;
  const scrollableRef = useRef<HTMLDivElement | null>(null);
  const outerFullHeightRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<ColumnState>(idle);
  const theme = useTheme();

  useEffect(() => {
    const outer = outerFullHeightRef.current;
    const scrollable = scrollableRef.current;
    const header = headerRef.current;
    const inner = innerRef.current;
    if (!outer || !scrollable || !header || !inner) {
      return;
    }

    // Pass the boardId to the column data
    const data = getColumnData({ column });

    function setIsCardOver({ data, location }: { data: any; location: DragLocationHistory }) {
      const innerHost = location.current.dropTargets[0];
      const isOverChildCard = Boolean(innerHost && isCardDropTargetData(innerHost.data));

      const proposed: ColumnState = {
        type: "is-card-over",
        dragging: data.rect,
        isOverChildCard,
      };
      setState((current) => {
        if (isShallowEqual(proposed, current)) {
          return current;
        }
        return proposed;
      });
    }

    return combine(
      draggable({
        element: header,
        getInitialData: () => data,
        onGenerateDragPreview({ source, location, nativeSetDragImage }) {
          const data = source.data;
          if (!isColumnData(data)) {
            return;
          }
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: preserveOffsetOnSource({ element: header, input: location.current.input }),
            render({ container }) {
              const rect = inner.getBoundingClientRect();
              const preview = inner.cloneNode(true);
              if (!preview) {
                return;
              }
              (preview as HTMLElement).style.width = `${rect.width}px`;
              (preview as HTMLElement).style.height = `${rect.height}px`;

              container.appendChild(preview);
            },
          });
        },
        onDragStart() {
          setState({ type: "is-dragging" });
        },
        onDrop() {
          setState(idle);
        },
      }),
      dropTargetForElements({
        element: outer,
        getData: () => data,
        canDrop({ source }) {
          return isDraggingACard({ source }) || isDraggingAColumn({ source });
        },
        getIsSticky: () => true,
        onDragStart({ source, location }) {
          if (isCardData(source.data)) {
            setIsCardOver({ data: source.data, location });
          }
        },
        onDragEnter({ source, location }) {
          if (isCardData(source.data)) {
            setIsCardOver({ data: source.data, location });
            return;
          }
          if (isColumnData(source.data) && source.data.column.id !== column.id) {
            setState({ type: "is-column-over" });
          }
        },
        onDropTargetChange({ source, location }) {
          if (isCardData(source.data)) {
            setIsCardOver({ data: source.data, location });
            return;
          }
        },
        onDragLeave({ source }) {
          if (isColumnData(source.data) && source.data.column.id === column.id) {
            return;
          }
          setState(idle);
        },
        onDrop() {
          setState(idle);
        },
      }),
      autoScrollForElements({
        canScroll({ source }) {
          return isDraggingACard({ source });
        },
        getConfiguration: () => ({ maxScrollSpeed: "standard" }),
        element: scrollable,
      }),
      unsafeOverflowAutoScrollForElements({
        element: scrollable,
        getConfiguration: () => ({ maxScrollSpeed: "standard" }),
        canScroll({ source }) {
          return isDraggingACard({ source });
        },
        getOverflow() {
          return {
            forTopEdge: {
              top: 1000,
            },
            forBottomEdge: {
              bottom: 1000,
            },
          };
        },
      })
    );
  }, [column]);

  return (
    <Box display="flex" width="100%" ref={outerFullHeightRef}>
      <Box display="flex" flexDirection="column" width="100%" ref={innerRef}>
        <Box display="flex" width="100%" flexDirection="column">
          <Box
            display="flex"
            flexDirection="column"
            gap="16px"
            padding={border ? "8px" : "0px"}
            bgcolor={selected ? theme.palette.custom.secondaryBackground : "none"}
            border={
              border
                ? `1px solid ${
                    selected ? theme.palette.custom.hyperlink : theme.palette.custom.secondaryBorder
                  }`
                : "none"
            }
            borderRadius="3px"
            onClick={onClick}
            sx={{ cursor: onClick ? "pointer" : "default" }}
          >
            {title && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ref={headerRef}
              >
                <Typography variant="h3">{title}</Typography>
                {rightComponent}
              </Box>
            )}
            <Box display="flex" flexDirection="column" gap="8px" ref={scrollableRef}>
              {cards.map((card, idx) => {
                const isObjectKey = card.type === "object_key";
                return (
                  <Card
                    key={card.ref || card.key || idx}
                    columnId={id}
                    card={card}
                    onClick={() =>
                      isObjectKey && onHideCard
                        ? onHideCard(idx)
                        : onRemoveCard
                        ? onRemoveCard(idx)
                        : () => {}
                    }
                    renderCard={renderCard ? renderCard(card, idx) : null}
                  />
                );
              })}
            </Box>
          </Box>
          {state.type === "is-card-over" && !state.isOverChildCard && <DragIndicator />}
        </Box>
      </Box>
    </Box>
  );
};
